import type {
  ModuleId,
  QuestionChoice,
  QuestionRange,
  SegmentT,
} from '@mentimeter/http-clients';
import type { QuestionModuleResult } from '@mentimeter/question-modules-types';
import { useCallback } from 'react';
import { usePreviewResultsIterator } from './usePreviewResultsIterator';
import { getPreviewResultsIteration as getChoicesPreviewResultsIteration } from './slide-types/choices/getPreviewResultsIteration';
import { getPreviewResultsIteration as getNumericalQuestionPreviewResultsIteration } from './slide-types/numerical-question/getPreviewResultsIteration';

interface useQuestionModulesPreviewResultsProps {
  moduleId: ModuleId;
  previewResultsEnabled: boolean;
  choices: QuestionChoice[];
  range?: QuestionRange | undefined;
  segments: SegmentT[] | undefined;
}

const DEFAULT_RANGE: QuestionRange = { min: 0, max: 10 };

export const useQuestionModulesPreviewResults = ({
  moduleId,
  previewResultsEnabled,
  choices,
  range,
  segments,
}: useQuestionModulesPreviewResultsProps) => {
  const getNextResults = useCallback(
    (iteration: number, previous: QuestionModuleResult | undefined) => {
      switch (moduleId) {
        case 'choices':
          if (previous === undefined || previous.type === 'choices') {
            return getChoicesPreviewResultsIteration({
              iteration,
              previous,
              extras: {
                type: 'choices',
                choices,
                segments,
              },
            });
          } else {
            return undefined;
          }
        case 'open':
          return undefined;
        case 'rating':
          return undefined;
        case 'ranking':
          return undefined;
        case '100-points':
          return undefined;
        case 'wordcloud':
          return undefined;
        case 'pin-on-image':
          return undefined;
        case 'numerical-question':
          if (previous === undefined || previous.type === 'scales') {
            return getNumericalQuestionPreviewResultsIteration({
              iteration,
              previous,
              extras: {
                type: 'scales',
                choices,
                range: range ?? DEFAULT_RANGE,
              },
            });
          } else {
            return undefined;
          }
        default:
          return undefined;
      }
    },
    [choices, range, segments, moduleId],
  );

  return usePreviewResultsIterator(previewResultsEnabled, getNextResults);
};
